import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Spinner, Form, InputGroup, Modal, Alert } from 'react-bootstrap';
import { FaBuilding, FaSearch, FaPlus, FaUsers, FaMapMarkerAlt, FaEye, FaKey, FaToggleOn, FaToggleOff, FaEdit } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import superadminAPI from '../../utils/superadminAPI';
import { FaBan } from 'react-icons/fa';
import { API_BASE_URL } from '../../config';


const CompanyAdminsList = () => {
  const [loading, setLoading] = useState(true);
  const [companyAdmins, setCompanyAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const navigate = useNavigate();
  const [showActiveModal, setShowActiveModal] = useState(false);
  const [activeCheckins, setActiveCheckins] = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeTotal, setActiveTotal] = useState(0);
  const [activeError, setActiveError] = useState('');
  
  // Force Checkout Reason Modal state
  const [showForceCheckoutModal, setShowForceCheckoutModal] = useState(false);
  const [forceCheckoutReason, setForceCheckoutReason] = useState('');
  const [selectedCheckInForCheckout, setSelectedCheckInForCheckout] = useState(null);
  const [forceCheckoutLoading, setForceCheckoutLoading] = useState(false);

  useEffect(() => {
    fetchCompanyAdmins();
  }, []);

  useEffect(() => {
    // Filter company admins based on search term
    if (searchTerm.trim() === '') {
      setFilteredAdmins(companyAdmins);
    } else {
      const filtered = companyAdmins.filter(
        admin => 
          admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          admin.company.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAdmins(filtered);
    }
  }, [searchTerm, companyAdmins]);

  const fetchCompanyAdmins = async () => {
    try {
      setLoading(true);
      const response = await superadminAPI.getAllCompanyAdmins();
      setCompanyAdmins(response.data);
      setFilteredAdmins(response.data);
    } catch (error) {
      console.error('Error fetching company admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (adminId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await superadminAPI.updateCompanyAdminStatus(adminId, newStatus);
      
      // Update the local state
      const updatedAdmins = companyAdmins.map(admin => {
        if (admin.id === adminId) {
          return { ...admin, status: newStatus };
        }
        return admin;
      });
      
      setCompanyAdmins(updatedAdmins);
      setFilteredAdmins(updatedAdmins);
      
      // Show success message
      alert(`Company account ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating company admin status:', error);
      // Show error message with details if available
      alert(`Error: ${error.response?.data?.message || 'Failed to update company status. Please try again.'}`);
    }
  };

  const handleExportCredentials = async (adminId) => {
    try {
      const response = await superadminAPI.exportCompanyAdminCredentials(adminId);
      
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `credentials_${adminId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting credentials:', error);
      alert(`Error: ${error.response?.data?.message || 'Failed to export credentials. Please try again.'}`);
    }
  };

  const handleEditClick = (admin) => {
    setEditingAdmin(admin);
    setEditFormData({
      name: admin.name,
      email: admin.email,
      phone: admin.phone || '',
      company: admin.company
    });
    setEditError('');
    setEditSuccess('');
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setEditLoading(true);
      setEditError('');
      setEditSuccess('');
      
      const response = await superadminAPI.updateCompanyAdmin(editingAdmin.id, editFormData);
      
      // Update the local state
      const updatedAdmins = companyAdmins.map(admin => {
        if (admin.id === editingAdmin.id) {
          return { ...admin, ...editFormData };
        }
        return admin;
      });
      
      setCompanyAdmins(updatedAdmins);
      setFilteredAdmins(updatedAdmins);
      
      setEditSuccess('Company admin updated successfully!');
      setTimeout(() => {
        setShowEditModal(false);
        setEditSuccess('');
      }, 2000);
      
    } catch (error) {
      console.error('Error updating company admin:', error);
      setEditError(error.response?.data?.message || 'Failed to update company admin. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  const fetchActiveCheckins = async (page = 1, limit = 100) => {
    try {
      setActiveLoading(true);
      setActiveError('');
      const resp = await superadminAPI.getAdminActiveCheckins(page, limit);
      console.log('Raw response:', resp); // Log full response
      console.log('Response data:', resp.data); // Log response.data
      const body = resp.data || {};
      
      // Log sample of checkin data to see structure
      if (Array.isArray(body.data) && body.data.length > 0) {
        console.log('Sample checkin record:', body.data[0]);
        console.log('Personnel name from user.name:', body.data[0].user?.name);
        console.log('Site name from site.name:', body.data[0].site?.name);
      }
      
      setActiveTotal(body.total || 0);
      setActiveCheckins(Array.isArray(body.data) ? body.data : []);
    } catch (err) {
      console.error('Failed to load active checkins:', err);
      if (err.response && err.response.status === 403) {
        setActiveError('Access denied: your account does not have permission to view active checkins (403).');
      } else if (err.response && err.response.data && err.response.data.message) {
        setActiveError(err.response.data.message);
      } else {
        setActiveError('Failed to load active checkins. Please try again.');
      }
    } finally {
      setActiveLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col xs={12} md={8}>
          <h1 className="mb-2 mb-md-4">Company Admins</h1>
          <p className="text-muted mb-0">Manage all company administrators in the FieldFlow platform.</p>
        </Col>
        <Col xs={12} md={4} className="d-flex align-items-center justify-content-start justify-content-md-end mt-3 mt-md-0">
          <Button 
            variant="primary" 
            className="d-flex align-items-center w-100 w-md-auto"
            onClick={() => navigate('/superadmin/company-admins/new')}
          >
            <FaPlus className="me-2" /> Add Company Admin
          </Button>
          <Button
            variant="outline-danger"
            className="d-flex align-items-center ms-2 w-100 w-md-auto"
            onClick={async () => {
              setShowActiveModal(true);
              setActiveError('');
              await fetchActiveCheckins(1, 100);
            }}
          >
            <FaBan className="me-2" /> Force Checkouts
          </Button>
        </Col>
      </Row>

      {/* Search and Filter */}
      <Row className="mb-3 mb-md-4">
        <Col xs={12} md={6}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search by company name, admin name, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Company Admins Table */}
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th className="d-none d-md-table-cell">Admin Name</th>
                      <th className="d-none d-lg-table-cell">Email</th>
                      <th className="d-none d-xl-table-cell">Phone</th>
                      <th className="d-none d-md-table-cell">Personnel</th>
                      <th className="d-none d-lg-table-cell">Sites</th>
                      <th className="d-none d-xl-table-cell">Created On</th>
                      <th className="d-none d-md-table-cell">Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAdmins.length > 0 ? (
                      filteredAdmins.map((admin) => (
                        <tr key={admin.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle bg-primary bg-opacity-10 p-1 p-md-2 me-2">
                                <FaBuilding className="text-primary" size={16} />
                              </div>
                              <div>
                                <div className="fw-bold">{admin.company}</div>
                                <small className="text-muted d-md-none">{admin.name}</small>
                                <small className="text-muted d-lg-none d-md-block">{admin.email}</small>
                              </div>
                            </div>
                          </td>
                          <td className="d-none d-md-table-cell">{admin.name}</td>
                          <td className="d-none d-lg-table-cell">{admin.email}</td>
                          <td className="d-none d-xl-table-cell">{admin.phone || 'N/A'}</td>
                          <td className="d-none d-md-table-cell">
                            <Badge bg="info" className="d-flex align-items-center" style={{ width: 'fit-content' }}>
                              <FaUsers className="me-1" /> {admin.personnelCount}
                            </Badge>
                          </td>
                          <td className="d-none d-lg-table-cell">
                            <Badge bg="warning" className="d-flex align-items-center" style={{ width: 'fit-content' }}>
                              <FaMapMarkerAlt className="me-1" /> {admin.sitesCount}
                            </Badge>
                          </td>
                          <td className="d-none d-xl-table-cell">{formatDate(admin.createdAt)}</td>
                          <td className="d-none d-md-table-cell">
                            <Badge bg={admin.status === 'active' ? 'success' : 'danger'}>
                              {admin.status === 'active' ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-1 gap-md-2 flex-wrap">
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                as={Link}
                                to={`/superadmin/company-admins/${admin.id}`}
                                title="View Details"
                                className="flex-fill flex-md-grow-0"
                              >
                                <FaEye />
                              </Button>
                              <Button 
                                variant="outline-info" 
                                size="sm"
                                onClick={() => handleEditClick(admin)}
                                title="Edit Company Details"
                                className="flex-fill flex-md-grow-0"
                              >
                                <FaEdit />
                              </Button>
                              <Button 
                                variant="outline-warning" 
                                size="sm"
                                onClick={() => handleExportCredentials(admin.id)}
                                title="Generate Credentials"
                                className="flex-fill flex-md-grow-0"
                              >
                                <FaKey />
                              </Button>
                              <Button 
                                variant={admin.status === 'active' ? 'outline-danger' : 'outline-success'} 
                                size="sm"
                                onClick={() => handleStatusToggle(admin.id, admin.status)}
                                title={admin.status === 'active' ? 'Deactivate' : 'Activate'}
                                className="flex-fill flex-md-grow-0"
                              >
                                {admin.status === 'active' ? <FaToggleOff /> : <FaToggleOn />}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center">
                          {searchTerm ? 'No matching company admins found' : 'No company admins found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Edit Company Admin Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Company Admin</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body>
            {editError && <Alert variant="danger">{editError}</Alert>}
            {editSuccess && <Alert variant="success">{editSuccess}</Alert>}
            
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FaBuilding className="me-2 text-primary" />
                    Company Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="company"
                    value={editFormData.company}
                    onChange={handleEditFormChange}
                    placeholder="Enter company name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FaUsers className="me-2 text-primary" />
                    Admin Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditFormChange}
                    placeholder="Enter admin name"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditFormChange}
                    placeholder="Enter email address"
                    required
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleEditFormChange}
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={editLoading}
            >
              {editLoading ? 'Updating...' : 'Update Company Admin'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      {/* Active Checkins / Force Checkout Modal */}
      <Modal show={showActiveModal} onHide={() => setShowActiveModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Active Checkins</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeError && <Alert variant="danger">{activeError}</Alert>}
          {activeLoading ? (
            <div className="text-center py-3"><Spinner animation="border" /></div>
          ) : (
            <div>
              <p className="small text-muted">Total active checkins: {activeTotal}</p>
              <div className="table-responsive">
                <Table size="sm" hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Personnel</th>
                      <th>Site</th>
                      <th>Checked In At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeCheckins.length > 0 ? activeCheckins.map((c, idx) => {
                      // Try multiple ways to get personnel name
                      const personnelName = (c.user && c.user.name) 
                        || c.personnelName 
                        || c.personnel?.name 
                        || c.userName
                        || 'Unknown Personnel';
                      
                      // Try multiple ways to get site name  
                      const siteName = (c.site && c.site.name)
                        || c.siteName 
                        || c.locationName
                        || 'Unknown Site';
                      
                      return (
                      <tr key={c.id || c.checkInId || idx}>
                        <td>{idx + 1}</td>
                        <td>{personnelName}</td>
                        <td>{siteName}</td>
                        <td>{c.checkedInAt ? new Date(c.checkedInAt).toLocaleString() : (c.checkInTime ? new Date(c.checkInTime).toLocaleString() : (c.createdAt ? new Date(c.createdAt).toLocaleString() : 'N/A'))}</td>
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setSelectedCheckInForCheckout(c);
                              setForceCheckoutReason('');
                              setShowForceCheckoutModal(true);
                            }}
                          >Force Checkout</Button>
                        </td>
                      </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan="5" className="text-center">No active checkins found</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActiveModal(false)}>Close</Button>
          <Button variant="primary" onClick={() => fetchActiveCheckins(1, 100)}>Refresh</Button>
        </Modal.Footer>
      </Modal>

      {/* Force Checkout Reason Modal */}
      <Modal show={showForceCheckoutModal} onHide={() => {
        setShowForceCheckoutModal(false);
        setForceCheckoutReason('');
        setSelectedCheckInForCheckout(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Force Checkout Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            You are about to force checkout <strong>
              {selectedCheckInForCheckout && (
                (selectedCheckInForCheckout.user && selectedCheckInForCheckout.user.name) 
                || selectedCheckInForCheckout.personnelName 
                || selectedCheckInForCheckout.personnel?.name 
                || selectedCheckInForCheckout.userName
                || 'this personnel'
              )}
            </strong>.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Reason for forced checkout:</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter reason for force checkout (optional)"
              value={forceCheckoutReason}
              onChange={(e) => setForceCheckoutReason(e.target.value)}
              disabled={forceCheckoutLoading}
            />
          </Form.Group>
          {activeError && <Alert variant="danger">{activeError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowForceCheckoutModal(false);
              setForceCheckoutReason('');
              setSelectedCheckInForCheckout(null);
            }}
            disabled={forceCheckoutLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={async () => {
              if (!selectedCheckInForCheckout) return;
              
              const reason = forceCheckoutReason;
              const c = selectedCheckInForCheckout;
                              try {
                                setForceCheckoutLoading(true);
                                setActiveError('');
                                const checkInId = c.id || c.checkInId;
                                
                                // Load draft data from localStorage
                                let draftData = {};
                                let attachmentData = {};
                                try { 
                                  draftData = JSON.parse(localStorage.getItem('reportDraft') || '{}'); 
                                } catch (e) {
                                  console.warn('Failed to parse reportDraft:', e);
                                }
                                try { 
                                  attachmentData = JSON.parse(localStorage.getItem('reportAttachment') || '{}'); 
                                } catch (e) {
                                  console.warn('Failed to parse reportAttachment:', e);
                                }

                                // Helper to upload a single base64 image and return its URL
                                async function uploadImage(dataUrl) {
                                  const blob = await fetch(dataUrl).then(res => res.blob());
                                  const uploadFormData = new FormData();
                                  uploadFormData.append('file', blob, 'photo.jpg');
                                  
                                  const resp = await fetch(`${API_BASE_URL}/api/upload`, {
                                    method: 'POST',
                                    headers: {
                                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                                    },
                                    body: uploadFormData
                                  });
                                  
                                  if (!resp.ok) {
                                    throw new Error(`Upload failed with status: ${resp.status}`);
                                  }
                                  
                                  const result = await resp.json();
                                  return result.url;
                                }

                                // Upload all captured images
                                let checkOutPhotos = [];
                                if (draftData.capturedImages && Array.isArray(draftData.capturedImages)) {
                                  console.log('[Force Checkout] Uploading', draftData.capturedImages.length, 'images...');
                                  
                                  const uploadPromises = draftData.capturedImages
                                    .filter(imgUrl => 
                                      typeof imgUrl === 'string' && 
                                      imgUrl.trim() !== '' && 
                                      imgUrl.startsWith('data:image/')
                                    )
                                    .map(async (imgUrl) => {
                                      try {
                                        return await uploadImage(imgUrl);
                                      } catch (e) {
                                        console.error('Failed to upload image:', e);
                                        return null;
                                      }
                                    });
                                  
                                  checkOutPhotos = (await Promise.all(uploadPromises)).filter(Boolean);
                                  console.log('[Force Checkout] Successfully uploaded', checkOutPhotos.length, 'photos');
                                }

                                // Handle attachment upload if exists
                                let attachmentUrl = null;
                                if (attachmentData.data && 
                                    typeof attachmentData.data === 'string' && 
                                    attachmentData.data.startsWith('data:')) {
                                  try {
                                    const blob = await fetch(attachmentData.data).then(res => res.blob());
                                    const attachmentFormData = new FormData();
                                    attachmentFormData.append('file', blob, attachmentData.name || 'attachment');
                                    
                                    const attachmentResp = await fetch(`${API_BASE_URL}/api/upload`, {
                                      method: 'POST',
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                      },
                                      body: attachmentFormData
                                    });
                                    
                                    if (attachmentResp.ok) {
                                      const result = await attachmentResp.json();
                                      attachmentUrl = result.url;
                                    }
                                  } catch (e) {
                                    console.warn('Failed to upload attachment:', e);
                                  }
                                }

                                // Build payload as JSON (photos are already uploaded)
                                const payload = {
                                  checkInId: checkInId,
                                  reason: reason || '',
                                  reportContent: draftData.report || '',
                                  checkOutPhotos: checkOutPhotos
                                };
                                
                                if (attachmentUrl) {
                                  payload.attachment = attachmentUrl;
                                }

                                console.log('[Force Checkout] Sending payload:', payload);

                                // Send force checkout request as JSON
                                await superadminAPI.forceCheckout(payload, false);
                                
                                // Update UI
                                setActiveCheckins(prev => prev.filter(x => (x.id || x.checkInId) !== checkInId));
                                setActiveTotal(prev => Math.max(0, prev - 1));
                                setActiveError('');
                                
                                // Clear localStorage drafts
                                localStorage.removeItem('reportDraft');
                                localStorage.removeItem('reportAttachment');
                                
                                alert('Force checkout completed successfully!');
                                
                                // Close the force checkout modal
                                setShowForceCheckoutModal(false);
                                setForceCheckoutReason('');
                                setSelectedCheckInForCheckout(null);
                              } catch (err) {
                                console.error('Failed to force checkout:', err);
                                setActiveError(err.response?.data?.message || 'Failed to force checkout. Please try again.');
                              } finally {
                                setForceCheckoutLoading(false);
                              }
            }}
            disabled={forceCheckoutLoading}
          >
            {forceCheckoutLoading ? 'Processing...' : 'Confirm Force Checkout'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CompanyAdminsList;
